<template>
  <div>
    <div class="d-flex justify-content-end">
      <div class="d-flex justify-content-between button-group">
        <img v-if="currentRoutePermissions.add_purchasecontract"
             src="@/assets/images/common-icons/icon_create.svg" @click="createContract"
             :title="$t('buttons.contracts.create_contract')">
        <img v-if="currentRoutePermissions.delete_purchasecontract"
             src="@/assets/images/common-icons/icon_delete.svg" @click="deleteContract"
             :title="$t('buttons.contracts.delete_contract')">
        <img src="@/assets/images/common-icons/icon_clear.png" @click="cleanFilters"
             :title="$t('buttons.common.clear_filters')">
        <img src="@/assets/images/common-icons/icon_search.svg" @click="search"
             :title="$t('buttons.common.search')">
      </div>
    </div>
    <div class="search-form">
      <div class="search-form__filters">
        <b-row>
          <b-col>
            <label>{{$t('labels.common.seller')}}</label>
            <multiselect v-model="searchData.sellers" :max-height="200"
                         placeholder="" label="name" track-by="name"
                         :options="sellers" :multiple="true"
                         :taggable="true" :close-on-select="false"></multiselect>
          </b-col>
          <b-col>
            <label>{{$t('labels.common.status')}}</label>
            <multiselect v-model="searchData.status" :max-height="200"
                         placeholder=""
                         :options="contractStatuses"
                         :taggable="true" :close-on-select="true"></multiselect>
          </b-col>
          <b-col>
            <label>{{$t('labels.common.buyer')}}</label>
            <multiselect v-model="searchData.buyers" :max-height="200"
                         placeholder="" label="name" track-by="name"
                         :options="customers" :multiple="true"
                         :taggable="true" :close-on-select="false"></multiselect>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <label>{{$t('labels.contracts.contract_number')}}</label>
            <input class="form-control" v-model="searchData.contract_number" @keyup.enter="search"
                   placeholder="">
          </b-col>
          <b-col>
            <label>{{$t('labels.contracts.contract_date')}}</label>
            <range-date-picker v-model="searchData.range_date" range lang="en"
                               placeholder=""
                               range-separator="/" :first-day-of-week="1"></range-date-picker>
          </b-col>
          <b-col>
            <label>{{$t('labels.common.creator')}}</label>
            <multiselect v-model="searchData.creator" :max-height="200"
                         placeholder="" label="username" track-by="id"
                         :options="users" :multiple="true"
                         :taggable="true" :close-on-select="false"></multiselect>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <label>{{$t('labels.contracts.contract_type')}}</label>
            <multiselect v-model="searchData.contract_type" :max-height="200"
                         placeholder=""
                         :options="contract_type"
                         :taggable="true" :close-on-select="true"></multiselect>
          </b-col>
          <b-col>
            <label>{{$t('labels.contracts.contract_expiration_date')}}</label>
            <range-date-picker v-model="searchData.delivery_term" range lang="en"
                               placeholder=""
                               range-separator="/" :first-day-of-week="1"></range-date-picker>
          </b-col>
          <b-col>
            <label>{{$t('labels.common.currency')}}</label>
            <multiselect v-model="searchData.currencies" :max-height="200"
                         placeholder="" label="code" track-by="code"
                         :options="currencies" :multiple="true"
                         :taggable="true" :close-on-select="false"></multiselect>
          </b-col>
        </b-row>
        <div>
          <b-row>
            <b-col cols="4">
              <input class="form-control"
                     placeholder=""
                     @input="setQuickFilter">
            </b-col>
            <b-col cols="4" offset="4" class="vertical-center-container d-flex justify-content-end">
              <label for="pagination-toggle" class="margin-right-5">{{$t('labels.common.pagination')}}</label>
              <input id="pagination-toggle" type="checkbox"
                     title="this checkbox needed for toggle pagination bar" v-model="paginationState"
                     @change="togglePagination">
              <save-columns-btn :grid="gridOptions" :grid-name="gridName" class="margin-left-5"></save-columns-btn>
            </b-col>
          </b-row>
        </div>
        <div>
          <b-row>
            <b-col>
              <ag-grid-vue class="ag-theme-balham grid" :gridOptions="gridOptions"
                           @rowDoubleClicked="goToContract" :pinnedBottomRowData="pinnedRowData"
                           @filterChanged="updateTotalData"
                           :columnDefs="columns" :rowData="contracts" :postProcessPopup="postProcessPopup">
              </ag-grid-vue>
            </b-col>
          </b-row>
          <b-row class="pagination__wrapper" v-if="paginationState">
            <b-col cols="5">
              <div class="pagination-info">{{countOfViewContracts}} {{$t('common_content.of')}} {{contractsCount}}
              </div>
            </b-col>
            <b-col cols="7">
              <b-pagination size="md"
                            :total-rows="contractsCount"
                            :per-page="searchObject.limit"
                            v-model="currentPaginateIndex"
                            @input="changePaginateState"
              ></b-pagination>
            </b-col>
          </b-row>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { AgGridVue } from 'ag-grid-vue';
  import {
    mapGetters,
    mapState
  } from 'vuex';
  import Multiselect from 'vue-multiselect';
  import RangeDatePicker from 'vue2-datepicker';
  import moment from 'moment';
  import SaveColumnsBtn from '@/components/common/SaveColumnsBtn';
  import {
    cleanFilters,
    saveFilters,
    setFilters,
    checkSavedFilterData,
    getArrayByProperty,
    setNotifyData
  } from '@/helpers/commonHelpers';
  import { notificationsRenderer } from '@/helpers/renderHelpers';
  import { gridMixins, updateTotalDataMixin } from '@/mixins';
  import { DATE_FORMAT } from '@/constants';

  export default {
    mixins: [gridMixins, updateTotalDataMixin],
    components: {
      Multiselect,
      RangeDatePicker,
      SaveColumnsBtn,
      'ag-grid-vue': AgGridVue
    },
    data() {
      return {
        contract_type: ['direct', 'exchange'],
        searchData: {
          sellers: [],
          contract_type: '',
          currencies: [],
          buyers: [],
          range_date: null,
          delivery_term: null,
          contract_number: '',
          status: '',
          creator: []
        },
        searchObject: {
          limit: 15,
          offset: 0
        },
        paginationState: true,
        currentPaginateIndex: 1
      }
    },
    methods: {
      async updateTotalData(event) {
        await this.updateTotalDataWith(event, async (params) => {
          await this.$store.dispatch('contracts/updateContractsTotal', params);
        })
      },
      search() {
        this.currentPaginateIndex = 1;
        this.searchObject = {
          ...this.searchObject,
          ...this.searchData,
          contract_date_between_after: this.searchData.range_date && this.searchData.range_date[0] ? moment(this.searchData.range_date[0]).format(DATE_FORMAT) : '',
          contract_date_between_before: this.searchData.range_date && this.searchData.range_date[1] ? moment(this.searchData.range_date[1]).format(DATE_FORMAT) : '',
          sellers: getArrayByProperty(this.searchData.sellers, 'id').toString(),
          buyers: getArrayByProperty(this.searchData.buyers, 'id').toString(),
          currencies: getArrayByProperty(this.searchData.currencies, 'id').toString(),
          creator: getArrayByProperty(this.searchData.creator, 'id').toString(),
          status: this.searchData.status,
          delivery_term_between_after: this.searchData.delivery_term && this.searchData.delivery_term[0] ? moment(this.searchData.delivery_term[0]).format(DATE_FORMAT) : '',
          delivery_term_between_before: this.searchData.delivery_term && this.searchData.delivery_term[1] ? moment(this.searchData.delivery_term[1]).format(DATE_FORMAT) : '',
        };
        this.$store.dispatch('contracts/loadContracts', this.searchObject);
        saveFilters('purchaseContracts', this.searchData);
      },
      createContract() {
        this.$router.push({path: `/contracts/new`});
      },
      deleteContract() {
        const id = getArrayByProperty(this.gridOptions.api.getSelectedRows(), 'id');
        if (id.length) {
          this.$http.delete(`purchase/contracts/`, {body: {id}}).then((response) => {
            this.$notify(setNotifyData('Success', response.body, 'success'));
            this.currentPaginateIndex = 1;
            this.$store.dispatch('contracts/loadContracts', this.searchObject);
          }).catch((error) => {
            console.log(error);
            notificationsRenderer(error.body, 'error');
          })
        } else {
          this.$notify(setNotifyData('Select contracts for removing', '', 'warn'));
        }
      },
      goToContract(params) {
        if (params.data && params.data.id) {
          this.$router.push({path: `/contracts/${params.data.id}`})
        }
      },
      cleanFilters() {
        cleanFilters(this.searchData);
      },
      setQuickFilter(e) {
        this.gridOptions.api.setQuickFilter(e.target.value);
      },
      changePaginateState() {
        const searchObject = {...this.searchObject, offset: (this.currentPaginateIndex - 1) * this.searchObject.limit};
        this.$store.dispatch('contracts/loadContracts', searchObject);
      },
      togglePagination() {
        if (!this.paginationState) {
          delete this.searchObject.limit;
          delete this.searchObject.offset;
        } else {
          this.searchObject.limit = 15;
          this.searchObject.offset = 0;
        }
        this.search();
      },
      clearData() {
        this.$store.commit('contracts/setData', []);
      }
    },
    beforeMount() {
      this.$store.dispatch('partners/getSellers');
      this.$store.dispatch('partners/getCustomers');
      this.$store.cache.dispatch('woodware/getUsers');
      this.$store.cache.dispatch('generic/getCurrencies');
      if (checkSavedFilterData('purchaseContracts')) {
        this.searchData = setFilters('purchaseContracts');
        this.search();
      }
    },
    beforeRouteEnter(to, from, next) {
      next(vm => {
        vm.clearData();
      });
    },
    computed: {
      ...mapState('contracts', ['gridName']),
      ...mapState('generic', ['currencies', 'currentRoutePermissions']),
      ...mapGetters('contracts', {
        gridOptions: 'gridOptions'
      }),
      ...mapState('contracts', ['contracts', 'columns', 'contractsCount', 'contractStatuses', 'pinnedRowData']),
      ...mapState('partners', ['sellers', 'customers']),
      ...mapState('woodware', ['users']),
      countOfViewContracts() {
        return this.contracts.length;
      }
    }
  }
</script>

<style scoped>

  .grid {
    height: calc(100vh - 400px);
  }

</style>



// WEBPACK FOOTER //
// src/components/pages/modules/purchases/contracts/PurchaseContractsList.vue